// MapTopBar — the persistent top-of-map command bar.
//
// Mirrors ForeFlight's pattern: fixed icon row + search bar that floats
// over the map without unmounting it. All controls communicate via
// callbacks so the parent (MapPage) owns which sheets are open.

import { Icon, IconName } from '../shell/Icon';
import { useNavigate } from 'react-router-dom';
import SearchTypeahead from './SearchTypeahead';
import { useUiStore } from '../stores/uiStore';
import { useIsDesktop } from '../shell/useBreakpoint';
// AccountContextSwitcher + PublicationsBadge are owned by the
// SideRail now (desktop rail + mobile drawer). Removed from MapTopBar
// imports 2026-05-13 to keep the top bar focused on map controls.
// import PublicationsBadge from './PublicationsBadge';
// import AccountContextSwitcher from '../components/AccountContextSwitcher';

interface Props {
  showLayers: boolean;
  onToggleLayers: () => void;
  showPlan: boolean;
  onTogglePlan: () => void;
  showInstruments: boolean;
  onToggleInstruments: () => void;
  onCenterOnGps: () => void;
  /** When true, follow-ownship is engaged (crosshair glows ember). */
  followOwnship?: boolean;
  /** When true, Direct-To mode is armed and the next tap commits. */
  dctMode?: boolean;
  onToggleDct?: () => void;
  /** Called when user picks a search hit. Parent zooms map + opens detail. */
  onSearchHit: (hit: import('../lib/search').SearchHit) => void;
  /** Called when the search field is submitted with a tail-number (N-number). */
  onTailLookup?: (ident: string) => void;
}

export default function MapTopBar({
  showLayers,
  onToggleLayers,
  showPlan,
  onTogglePlan,
  showInstruments,
  onToggleInstruments,
  onCenterOnGps,
  followOwnship,
  dctMode,
  onToggleDct,
  onSearchHit,
  onTailLookup,
}: Props) {
  const nav = useNavigate();
  const isDesktop = useIsDesktop();

  if (isDesktop) {
    // Desktop: single row of buttons + a fixed-width search box on the
    // right. No hamburger (the rail is permanent), no centered spacers.
    return (
      <div className="absolute z-30 left-0 right-0 top-0" style={{ paddingTop: 'var(--safe-top)' }}>
        <div className="flex items-center gap-2 px-3 pt-2">
          <BarButton icon="layers" label="Layers" active={showLayers} isToggle onClick={onToggleLayers} />
          <BarButton icon="route" label="Flight Plan" active={showPlan} isToggle onClick={onTogglePlan} />
          {onToggleDct && (
            <BarButton icon="direct-to" label="Direct To" active={!!dctMode} isToggle onClick={onToggleDct} />
          )}
          <BarButton icon="gauge" label="Instruments" active={showInstruments} isToggle onClick={onToggleInstruments} />
          {/* AccountContextSwitcher + PublicationsBadge live in the
              SideRail (switcher under the logo, badge above version
              footer). One source of truth for both. */}
          <BarButton icon="gear" label="Settings" onClick={() => nav('/settings')} />

          <div className="flex-1" />

          <BarButton icon="crosshair" label="Center on GPS" active={followOwnship} isToggle onClick={onCenterOnGps} />

          <div className="w-80 xl:w-96">
            <SearchTypeahead onSelect={onSearchHit} onTailLookup={onTailLookup} />
          </div>
        </div>
      </div>
    );
  }

  // Mobile: two rows (icon strip on top, full-width search beneath).
  return (
    <div
      className="absolute z-30 left-0 right-0 top-0"
      style={{ paddingTop: 'var(--safe-top)' }}
    >
      {/* Row 1: icon command bar */}
      <div className="flex items-center gap-1 px-2 pt-2">
        <HamburgerButton />
        <Spacer />
        <BarButton icon="layers" label="Layers" active={showLayers} isToggle onClick={onToggleLayers} />
        <BarButton icon="route" label="Flight Plan" active={showPlan} isToggle onClick={onTogglePlan} />
        {onToggleDct && (
          <BarButton icon="direct-to" label="Direct To" active={!!dctMode} isToggle onClick={onToggleDct} />
        )}
        <BarButton icon="gauge" label="Instruments" active={showInstruments} isToggle onClick={onToggleInstruments} />
        {/* AccountContextSwitcher + PublicationsBadge moved to the
            mobile SideRail drawer 2026-05-13 (header + footer
            respectively). Boss req: 'move to the menu bar in the
            upper left somewhere like on desktop'. Keeps the always-
            visible top bar lean and mirrors the desktop pattern. */}
        <BarButton icon="gear" label="Settings" onClick={() => nav('/settings')} />
        <Spacer />
        <BarButton icon="crosshair" label="Center on GPS" active={followOwnship} isToggle onClick={onCenterOnGps} />
      </div>
      {/* Row 2: full-width search */}
      <div className="px-2 pt-2">
        <SearchTypeahead onSelect={onSearchHit} onTailLookup={onTailLookup} />
      </div>
    </div>
  );
}

function HamburgerButton() {
  return (
    <button
      onClick={() => useUiStore.getState().toggleRail()}
      className="h-10 w-10 bg-cockpit-800/90 backdrop-blur border border-cockpit-600 rounded-lg flex items-center justify-center text-cockpit-200"
      aria-label="Menu"
    >
      <Icon name="menu" />
    </button>
  );
}

function Spacer() {
  return <div className="flex-1" />;
}

function BarButton({
  icon,
  label,
  active,
  isToggle,
  onClick,
}: {
  icon: IconName;
  label: string;
  active?: boolean;
  /** When true, button announces toggle state via aria-pressed. */
  isToggle?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      aria-pressed={isToggle ? !!active : undefined}
      title={label}
      className={[
        'h-10 px-2.5 rounded-lg flex items-center gap-1.5 backdrop-blur border text-sm',
        active
          ? 'bg-ember-600/90 border-ember-500 text-white'
          : 'bg-cockpit-800/90 border-cockpit-600 text-cockpit-200',
      ].join(' ')}
    >
      <Icon name={icon} />
      <span className="hidden md:inline text-[12px]">{label}</span>
    </button>
  );
}
